<script lang="ts">
  import { onMount, onDestroy, afterUpdate } from "svelte";
  import CrossIcon from "../utils/assets/cross_icon.svelte";
  import ChevronLeftIcon from "../utils/assets/chevron_left_icon.svelte";
  import ChevronRightIcon from "../utils/assets/chevron_right_icon.svelte";
  import DownloadIcon from "../utils/assets/download_icon.svelte";
  import ClipLoader from "../utils/assets/clip_loader.svelte";
  import {
    downloadFile,
    isImage,
    isPdf,
    isText,
    isMicrosoftOffice,
    isTextFile,
    getDateAndTime,
    getFileTypeIcon,
  } from "../utils/functions";
  import {
    attachmentModalOpen,
    attachments,
    attachmentModalInitialIndex,
    attachmentModalTimestamp,
  } from "../stores/chat";

  const MAX_THUMBNAILS_VISIBLE = 5;
  const THUMBNAIL_WIDTH = 48;
  const THUMBNAIL_GAP = 8;
  const MAX_THUMBNAILS_WIDTH =
    MAX_THUMBNAILS_VISIBLE * THUMBNAIL_WIDTH +
    (MAX_THUMBNAILS_VISIBLE - 1) * THUMBNAIL_GAP;

  let currentIndex = $attachmentModalInitialIndex;
  let thumbsScrollContainer: HTMLDivElement;
  let textContent = "";
  let loading = false;
  let error = "";
  let isDownloading = false;

  $: current = $attachments[currentIndex];
  $: currentIndex = $attachmentModalInitialIndex;
  $: timestamp = getDateAndTime($attachmentModalTimestamp);
  $: fileIconUrl =
    getFileTypeIcon(current?.file_name, current?.content_type) ??
    "https://vf-assets.verbaflo.com/vf-email/vf-email/doc.svg";

  const prev = () => {
    const newIndex =
      (currentIndex - 1 + $attachments.length) % $attachments.length;
    attachmentModalInitialIndex.set(newIndex);
    setTimeout(() => scrollToActiveThumbnail(), 0);
  };

  const next = () => {
    const newIndex = (currentIndex + 1) % $attachments.length;
    attachmentModalInitialIndex.set(newIndex);
    setTimeout(() => scrollToActiveThumbnail(), 0);
  };

  const handleDownload = async () => {
    isDownloading = true;
    try {
      const file = $attachments[currentIndex];
      if (!file?.file_url) return;
      await downloadFile(file.file_url, file.file_name);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      isDownloading = false;
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (!$attachmentModalOpen) return;
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
    if (e.key === "Escape") handleClose();
  };

  const handleClose = () => {
    attachmentModalOpen.set(false);
    attachmentModalInitialIndex.set(0);
    attachmentModalTimestamp.set("");
    attachments.set([]);
  };

  const handleOverlayClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const scrollToActiveThumbnail = () => {
    if (!thumbsScrollContainer) return;
    const activeEl = thumbsScrollContainer.querySelector(
      `#attachment-thumb-${currentIndex}`
    ) as HTMLElement | null;
    if (!activeEl) return;
    activeEl.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  const fetchTextContent = async (fileUrl: string) => {
    try {
      loading = true;
      error = "";
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      const text = await response.text();
      textContent = text;
    } catch (err) {
      console.error("Error fetching text file:", err);
      error = err instanceof Error ? err.message : "Failed to load file";
    } finally {
      loading = false;
    }
  };

  $: if (
    $attachmentModalOpen &&
    current &&
    (isText(current) || isTextFile(current))
  ) {
    fetchTextContent(current.file_url);
  }

  $: if ($attachmentModalOpen) {
    scrollToActiveThumbnail();
  }

  onMount(() => {
    if ($attachmentModalOpen) {
      document.addEventListener("keydown", handleKeydown);
    }
  });

  onDestroy(() => {
    document.removeEventListener("keydown", handleKeydown);
  });

  afterUpdate(() => {
    if ($attachmentModalOpen) {
      document.addEventListener("keydown", handleKeydown);
    } else {
      document.removeEventListener("keydown", handleKeydown);
    }
  });
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
{#if $attachmentModalOpen}
  <div
    class="verbaflo-modal-overlay"
    on:click={handleOverlayClick}
    on:keydown={(e) => {
      if (e.key === "Escape") handleClose();
    }}
    role="presentation"
    tabindex="-1"
  >
    <div
      class="verbaflo-modal-content"
      on:click|stopPropagation
      on:keydown|stopPropagation
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        class="verbaflo-modal-header"
        role="banner"
        on:click|stopPropagation
        on:keydown|stopPropagation
      >
        <div class="verbaflo-modal-title-section">
          <div class="verbaflo-modal-title-row">
            <img
              src={fileIconUrl}
              alt="File Icon"
              class="verbaflo-modal-file-icon"
            />
            <div class="verbaflo-modal-title-row-content">
              <div class="verbaflo-modal-filename">
                {current?.file_name || "Attachment"}
              </div>
              <div class="verbaflo-modal-timestamp">
                {timestamp?.date}
                <div class="verbaflo-modal-timestamp-divider" />
                {timestamp?.time}
              </div>
            </div>
          </div>
        </div>
        <div class="verbaflo-modal-header-actions">
          <button
            class="verbaflo-modal-download-btn"
            on:click={handleDownload}
            aria-label="Download file"
            disabled={isDownloading}
          >
            {#if isDownloading}
              <ClipLoader size={14} thickness={2} speed={1} color="#1c6cff" />
            {:else}
              <DownloadIcon size="14" color="#1c6cff" />
            {/if}
            <span class="verbaflo-modal-download-text">Download</span>
          </button>
          <button
            class="verbaflo-modal-close-btn"
            on:click={handleClose}
            aria-label="Close modal"
          >
            <div class="verbaflo-modal-close-icon-wrapper">
              <CrossIcon size="11" color="#252B37" strokeWidth="2" />
            </div>
          </button>
        </div>
      </div>

      <div class="verbaflo-modal-body">
        <div class="verbaflo-modal-preview-container">
          <div class="verbaflo-modal-preview">
            {#if isImage(current)}
              <img
                src={current.file_url}
                alt={current.file_name}
                class="verbaflo-modal-image"
              />
            {:else if isPdf(current)}
              <object
                data={current.file_url}
                type="application/pdf"
                class="verbaflo-modal-pdf"
                title={current.file_name || "PDF Preview"}
                aria-label={current.file_name || "PDF Preview"}
              >
                <iframe
                  src={current.file_url}
                  class="verbaflo-modal-pdf"
                  title={current.file_name || "PDF Preview"}
                />
              </object>
            {:else if isText(current) || isTextFile(current)}
              {#if loading}
                <div class="verbaflo-modal-text-loading">
                  <div class="verbaflo-modal-spinner" />
                  <p class="verbaflo-modal-loading-text">
                    Loading {current.file_name}...
                  </p>
                </div>
              {:else if error}
                <div class="verbaflo-modal-text-error">
                  <p class="verbaflo-modal-error-title">
                    Failed to load text file
                  </p>
                  <p class="verbaflo-modal-error-message">{error}</p>
                  <a
                    href={current.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="verbaflo-modal-error-link"
                  >
                    Open in new tab
                  </a>
                </div>
              {:else}
                <div class="verbaflo-modal-text-container">
                  <pre class="verbaflo-modal-text-content">{textContent}</pre>
                </div>
              {/if}
            {:else if isMicrosoftOffice(current)}
              <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                  current.file_url
                )}&embedded=true`}
                class="verbaflo-modal-iframe"
                title={current.file_name || "Office Document Preview"}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            {:else}
              <iframe
                src={current.file_url}
                class="verbaflo-modal-iframe"
                title={current.file_name || "File Preview"}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            {/if}
          </div>
        </div>

        {#if $attachments.length > 0}
          <div class="verbaflo-modal-thumbnails-section">
            {#if $attachments.length > 1}
              <button
                class="verbaflo-modal-nav-btn"
                on:click={prev}
                aria-label="Previous attachment"
              >
                <ChevronLeftIcon />
              </button>
            {/if}

            <div
              bind:this={thumbsScrollContainer}
              class="verbaflo-modal-thumbnails"
              style="max-width: {MAX_THUMBNAILS_WIDTH}px;"
            >
              {#each $attachments as att, idx}
                {@const fileIconUrl =
                  getFileTypeIcon(att.file_name, att.content_type) ??
                  "https://vf-assets.verbaflo.com/vf-email/vf-email/doc.svg"}
                <button
                  id="attachment-thumb-{idx}"
                  class="verbaflo-modal-thumb"
                  class:verbaflo-modal-thumb-active={idx === currentIndex}
                  on:click={() => attachmentModalInitialIndex.set(idx)}
                  aria-label="Open {att.file_name || 'attachment'}"
                >
                  {#if isImage(att)}
                    <div class="verbaflo-modal-thumb-image-wrapper">
                      <img
                        src={att.file_url}
                        alt={att.file_name || "attachment"}
                        class="verbaflo-modal-thumb-image"
                      />
                    </div>
                  {:else}
                    <img
                      src={fileIconUrl}
                      alt={att.file_name || "attachment"}
                      class="verbaflo-modal-thumb-file-icon"
                    />
                  {/if}
                </button>
              {/each}
            </div>

            {#if $attachments?.length > 1}
              <button
                class="verbaflo-modal-nav-btn"
                on:click={next}
                aria-label="Next attachment"
              >
                <ChevronRightIcon />
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  :global(#vfwidget .verbaflo-modal-overlay) {
    position: fixed !important;
    inset: 0 !important;
    background: rgba(0, 0, 0, 0.8) !important;
    z-index: 2147483647 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-modal-content) {
    background: #ffffff !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
    width: 80dvw !important;
    max-width: 1200px !important;
    height: 80dvh !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  :global(#vfwidget .verbaflo-modal-header) {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    padding: 18px !important;
    gap: 16px !important;
  }

  :global(#vfwidget .verbaflo-modal-title-section) {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
    min-width: 0 !important;
    flex: 1 !important;
  }

  :global(#vfwidget .verbaflo-modal-title-row) {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    min-width: 0 !important;
  }

  :global(#vfwidget .verbaflo-modal-title-row-content) {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    justify-content: center !important;
    gap: 3px !important;
  }

  :global(#vfwidget .verbaflo-modal-timestamp) {
    font-size: 12px !important;
    color: #898989 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
  }

  :global(#vfwidget .verbaflo-modal-timestamp-divider) {
    width: 1px !important;
    height: 10px !important;
    background: #898989 !important;
  }

  :global(#vfwidget .verbaflo-modal-filename) {
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #222222 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 60dvw !important;
  }

  :global(#vfwidget .verbaflo-modal-header-actions) {
    display: flex !important;
    align-items: center !important;
    gap: 24px !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-modal-download-btn) {
    all: unset !important;
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-modal-download-text) {
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #1c6cff !important;
  }

  :global(#vfwidget .verbaflo-modal-close-btn) {
    all: unset !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-modal-close-icon-wrapper) {
    width: 16px !important;
    height: 16px !important;
    background: #dfe0e2 !important;
    border-radius: 50% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-modal-body) {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 24px !important;
    padding-bottom: 18px !important;
    overflow: hidden !important;
    padding-left: 18px !important;
    padding-right: 18px !important;
  }

  :global(#vfwidget .verbaflo-modal-preview-container) {
    flex: 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #ffffff !important;
    overflow: hidden !important;
  }

  :global(#vfwidget .verbaflo-modal-preview) {
    width: 100% !important;
    height: 55dvh !important;
    max-height: 55dvh !important;
    min-height: 55dvh !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-modal-image) {
    max-height: 55dvh !important;
    max-width: 100% !important;
    object-fit: contain !important;
    border-radius: 8px !important;
  }

  :global(#vfwidget .verbaflo-modal-pdf) {
    width: 100% !important;
    height: 55dvh !important;
    border-radius: 8px !important;
    border: none !important;
  }

  :global(#vfwidget .verbaflo-modal-text-loading),
  :global(#vfwidget .verbaflo-modal-text-error) {
    width: 100% !important;
    height: 55dvh !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    background: #f9fafb !important;
    border-radius: 8px !important;
  }

  :global(#vfwidget .verbaflo-modal-spinner) {
    width: 32px !important;
    height: 32px !important;
    border: 2px solid #e5e7eb !important;
    border-top-color: #252b37 !important;
    border-radius: 50% !important;
    animation: spin 1s linear infinite !important;
    margin-bottom: 8px !important;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  :global(#vfwidget .verbaflo-modal-loading-text) {
    font-size: 14px !important;
    color: #6b7280 !important;
  }

  :global(#vfwidget .verbaflo-modal-error-title) {
    font-size: 14px !important;
    color: #ef4444 !important;
    margin-bottom: 8px !important;
  }

  :global(#vfwidget .verbaflo-modal-error-message) {
    font-size: 12px !important;
    color: #6b7280 !important;
  }

  :global(#vfwidget .verbaflo-modal-error-link) {
    font-size: 12px !important;
    color: #252b37 !important;
    text-decoration: underline !important;
    margin-top: 8px !important;
  }

  :global(#vfwidget .verbaflo-modal-text-container) {
    width: 100% !important;
    height: 55dvh !important;
    background: #ffffff !important;
    border-radius: 8px !important;
    border: 1px solid #e5e7eb !important;
    overflow: hidden !important;
  }

  :global(#vfwidget .verbaflo-modal-text-content) {
    width: 100% !important;
    height: 100% !important;
    overflow: auto !important;
    padding: 16px !important;
    font-size: 14px !important;
    color: #1f2937 !important;
    white-space: pre-wrap !important;
    font-family: monospace !important;
    line-height: 1.6 !important;
    margin: 0 !important;
  }

  :global(#vfwidget .verbaflo-modal-iframe) {
    width: 100% !important;
    height: 55dvh !important;
    border-radius: 8px !important;
    border: 1px solid #e5e7eb !important;
  }

  :global(#vfwidget .verbaflo-modal-thumbnails-section) {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 24px !important;
  }

  :global(#vfwidget .verbaflo-modal-nav-btn) {
    all: unset !important;
    width: 26px !important;
    height: 26px !important;
    border-radius: 50% !important;
    border: 1px solid #e5e7eb !important;
    background: #ffffff !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    flex-shrink: 0 !important;
    transition: opacity 0.2s !important;
  }

  :global(#vfwidget .verbaflo-modal-nav-btn:hover) {
    opacity: 0.9 !important;
  }

  :global(#vfwidget .verbaflo-modal-nav-btn svg) {
    width: 14px !important;
    height: 14px !important;
  }

  :global(#vfwidget .verbaflo-modal-nav-btn svg path) {
    stroke: #252b37 !important;
  }

  :global(#vfwidget .verbaflo-modal-thumbnails) {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    overflow-x: auto !important;
    scroll-snap-type: x mandatory !important;
    scrollbar-width: none !important;
  }

  :global(#vfwidget .verbaflo-modal-thumbnails::-webkit-scrollbar) {
    display: none !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb) {
    all: unset !important;
    width: 48px !important;
    height: 48px !important;
    padding: 1.5px !important;
    border-radius: 4px !important;
    overflow: hidden !important;
    flex-shrink: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: 1px solid #e5e7eb !important;
    cursor: pointer !important;
    scroll-snap-align: center !important;
    transition:
      opacity 0.2s,
      border-color 0.2s !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb-image-wrapper) {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb:hover) {
    opacity: 0.9 !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb-active) {
    border-color: #1c6cff !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb-image) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 2px !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb svg) {
    width: 32px !important;
    height: 32px !important;
  }

  :global(#vfwidget .verbaflo-modal-file-icon) {
    width: 20px !important;
    height: 20px !important;
    object-fit: contain !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-modal-thumb-file-icon) {
    width: 32px !important;
    height: 32px !important;
    object-fit: contain !important;
    flex-shrink: 0 !important;
  }

  @media only screen and (max-width: 768px) {
    :global(#vfwidget .verbaflo-modal-header-actions) {
      gap: 12px !important;
    }
  }
</style>
